import type {
  Assignment, CallOutcome, Customer, LogEntry, LogType, NewBizProspect,
  NewBizSource, PipelineStatus, Quote, QuoteStatus, Role, User
} from './types';

async function http<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'same-origin'
  });
  if (!res.ok) {
    let msg = res.statusText;
    try { const j = await res.json() as { error?: string }; if (j.error) msg = j.error; } catch { /* ignore */ }
    throw new Error(msg || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return await res.json() as T;
}

export interface MeResponse { user: User | null }
export interface LoginStaffBody { mode: 'staff'; username: string; password: string }
export interface LoginAdminBody { mode: 'admin'; password: string }

export const api = {
  me: () => http<MeResponse>('GET', '/api/auth/me'),
  login: (body: LoginStaffBody | LoginAdminBody) => http<{ user: User }>('POST', '/api/auth/login', body),
  logout: () => http<void>('POST', '/api/auth/logout'),

  listCustomers: () => http<Customer[]>('GET', '/api/customers'),
  addCustomer: (c: { name: string; amName: string; contact?: string; phone?: string; email?: string }) =>
    http<Customer>('POST', '/api/customers', c),
  deleteCustomer: (id: string) => http<void>('DELETE', `/api/customers/${encodeURIComponent(id)}`),

  listLogs: () => http<LogEntry[]>('GET', '/api/logs'),
  addLog: (l: { type: LogType; customerId: string; notes: string }) => http<LogEntry>('POST', '/api/logs', l),
  updateLog: (id: string, body: { type?: LogType; notes?: string }) =>
    http<LogEntry>('PATCH', `/api/logs/${encodeURIComponent(id)}`, body),
  deleteLog: (id: string) => http<void>('DELETE', `/api/logs/${encodeURIComponent(id)}`),

  listUsers: () => http<User[]>('GET', '/api/users'),
  addUser: (u: { name: string; username: string; email: string; role: Role; password: string }) =>
    http<User>('POST', '/api/users', u),
  deleteUser: (id: string) => http<void>('DELETE', `/api/users/${encodeURIComponent(id)}`),

  listAssignments: () => http<Assignment[]>('GET', '/api/assignments'),
  updateAssignment: (id: string, body: { status?: PipelineStatus; outcome?: CallOutcome | null; revenue?: number; notes?: string; amName?: string }) =>
    http<Assignment>('PATCH', `/api/assignments/${encodeURIComponent(id)}`, body),
  deleteAssignment: (id: string) => http<void>('DELETE', `/api/assignments/${encodeURIComponent(id)}`),

  listNewBiz: () => http<NewBizProspect[]>('GET', '/api/newbiz'),
  addNewBiz: (b: { amName?: string; companyName: string; contact?: string; phone?: string; email?: string; source?: NewBizSource; notes?: string }) =>
    http<NewBizProspect>('POST', '/api/newbiz', b),
  updateNewBiz: (id: string, body: { status?: PipelineStatus; outcome?: CallOutcome | null; revenue?: number; notes?: string; amName?: string }) =>
    http<NewBizProspect>('PATCH', `/api/newbiz/${encodeURIComponent(id)}`, body),
  deleteNewBiz: (id: string) => http<void>('DELETE', `/api/newbiz/${encodeURIComponent(id)}`),

  bulkAutoAssign: (body: { mode: 'all' } | { mode: 'am'; amName: string }) =>
    http<{ assigned: number; message: string; details?: string[] }>('POST', '/api/bulk/auto-assign', body),
  bulkClearCycle: (amName: string) => http<{ removed: number; message: string }>('POST', '/api/bulk/clear-cycle', { amName }),
  bulkMarkOverdueLost: () => http<{ updated: number; message: string }>('POST', '/api/bulk/mark-overdue-lost', {}),

  listQuotes: () => http<Quote[]>('GET', '/api/quotes'),
  addQuote: (q: { customerId: string; clarityRef: string; notes?: string; revenue?: number }) =>
    http<Quote>('POST', '/api/quotes', q),
  updateQuote: (id: string, body: { status?: QuoteStatus; revenue?: number; notes?: string; clarityRef?: string }) =>
    http<Quote>('PATCH', `/api/quotes/${encodeURIComponent(id)}`, body),
  deleteQuote: (id: string) => http<void>('DELETE', `/api/quotes/${encodeURIComponent(id)}`)
};
